import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '../cn';

import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import type { BrandInfo, NavEntry } from './types';

interface Props {
  brand: BrandInfo;
  brandTo: string;
  nav: NavEntry[];
  settingsTo?: string;
  notificationsTo?: string;
  signOutRedirect?: string;
  /** Where the profile dropdown's "Profile" item navigates. */
  profilePath?: string;
  /** Where the profile dropdown's "Preferences" item navigates. Hidden when undefined. */
  preferencesPath?: string;
  /** Optional slot rendered as a full-width strip pinned above the sidebar
   * and topbar. The slot's height is fixed via `--top-banner-h` so the
   * sidebar and topbar can offset themselves predictably; keep the rendered
   * element to that height. */
  topBanner?: ReactNode;
  /** Optional control rendered in the topbar (e.g. a workspace switcher). */
  tenantSwitcher?: ReactNode;
  appClassName?: string;
  collapsedKey?: string;
  children: ReactNode;
}

const TOP_BANNER_H = '2.75rem';

function readCollapsed(key: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function WorkspaceShell({
  brand,
  brandTo,
  nav,
  settingsTo,
  notificationsTo,
  signOutRedirect,
  profilePath,
  preferencesPath,
  topBanner,
  tenantSwitcher,
  appClassName,
  collapsedKey = 'saas-starter.sidebar.collapsed',
  children,
}: Props): JSX.Element {
  const { t } = useTranslation('common');
  const location = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed(collapsedKey));
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(collapsedKey, collapsed ? '1' : '0');
    } catch {
      /* private mode — ignore */
    }
  }, [collapsed, collapsedKey]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const sidebarWidth = collapsed ? 'md:w-16' : 'md:w-60';
  const mainOffset = collapsed ? 'md:ms-16' : 'md:ms-60';

  // Banner-aware offsets. The banner is fixed top:0 full-width so it survives
  // any inner scroll container; the sidebar's `top` and the Topbar's `top`
  // both follow the same CSS var so we never have to hand-thread the height.
  const topOffset = topBanner ? `var(--top-banner-h)` : '0px';
  const cssVars = topBanner
    ? ({ ['--top-banner-h' as string]: TOP_BANNER_H } as React.CSSProperties)
    : undefined;

  return (
    <div
      className={cn('min-h-screen w-full bg-muted/30', appClassName)}
      style={{ ...cssVars, paddingTop: topOffset }}
    >
      {topBanner ? (
        <div
          data-testid="top-banner-slot"
          className="fixed inset-x-0 top-0 z-50"
          style={{ height: TOP_BANNER_H }}
        >
          {topBanner}
        </div>
      ) : null}
      <aside
        aria-label={t('nav.aria_main')}
        data-collapsed={collapsed}
        className={cn(
          'fixed bottom-0 start-0 z-30 hidden border-e border-border bg-background transition-[width] duration-200 md:flex md:flex-col',
          sidebarWidth,
        )}
        style={{ top: topOffset }}
      >
        <Sidebar
          brand={brand}
          brandTo={brandTo}
          nav={nav}
          {...(settingsTo !== undefined ? { settingsTo } : {})}
          collapsed={collapsed}
          {...(signOutRedirect !== undefined ? { signOutRedirect } : {})}
        />
      </aside>

      <div className={cn('flex min-h-screen flex-col transition-[margin] duration-200', mainOffset)}>
        <Topbar
          brand={brand}
          brandTo={brandTo}
          nav={nav}
          {...(settingsTo !== undefined ? { settingsTo } : {})}
          pageTitle={brand.name}
          mobileOpen={mobileOpen}
          onMobileOpenChange={setMobileOpen}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((v) => !v)}
          {...(tenantSwitcher !== undefined ? { tenantSwitcher } : {})}
          {...(notificationsTo !== undefined ? { notificationsTo } : {})}
          {...(signOutRedirect !== undefined ? { signOutRedirect } : {})}
          {...(profilePath !== undefined ? { profilePath } : {})}
          {...(preferencesPath !== undefined ? { preferencesPath } : {})}
        />
        <main className="flex-1 px-4 py-6 md:px-8 md:py-8">
          <div className="w-full min-w-0 space-y-6">{children}</div>
        </main>
      </div>
    </div>
  );
}
